import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo } from 'react';

export interface UseVirtualizationProps<T> {
    disabled?: boolean;
    overscan?: number;
    estimateRowHeight?: number | ((index: number) => number);
    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    data?: T[];
    measureDeps?: unknown[];
}

const MIN_ITEMS_TO_VIRTUALIZE = 10;

export const useVirtualization = <T>({
    disabled,
    overscan = 5,
    estimateRowHeight = 50,
    scrollContainerRef,
    data,
    measureDeps = [],
}: UseVirtualizationProps<T>) => {
    const shouldVirtualize = !disabled && !!data && data.length > MIN_ITEMS_TO_VIRTUALIZE;
    const virtualCount = shouldVirtualize ? data.length : 0;

    const estimateSize = useMemo(
        () => (typeof estimateRowHeight === 'function' ? estimateRowHeight : () => estimateRowHeight),
        [estimateRowHeight]
    );

    const getScrollElement = useCallback(
        () => (shouldVirtualize ? scrollContainerRef?.current || null : null),
        [shouldVirtualize, scrollContainerRef]
    );

    const activeRowVirtualizer = useVirtualizer({
        enabled: shouldVirtualize,
        count: virtualCount,
        estimateSize,
        overscan,
        getScrollElement,
    });

    const virtualRows = shouldVirtualize && activeRowVirtualizer ? activeRowVirtualizer.getVirtualItems() : [];

    const measureElementRef = activeRowVirtualizer?.measureElement;
    const virtualPaddingTop = shouldVirtualize && virtualRows.length > 0 ? virtualRows[0].start : 0;
    const virtualPaddingBottom =
        shouldVirtualize && activeRowVirtualizer && virtualRows.length > 0
            ? activeRowVirtualizer.getTotalSize() - virtualRows.at(-1)!.end
            : 0;

    useEffect(() => {
        if (shouldVirtualize && activeRowVirtualizer) {
            activeRowVirtualizer.measure();
        }
    }, [virtualCount, shouldVirtualize, activeRowVirtualizer, ...measureDeps]);

    return {
        shouldVirtualize,
        virtualPaddingTop,
        virtualPaddingBottom,
        measureElementRef,
        virtualRows,
    };
};
